import { resolvePerformanceBriefTemplate, type PerformanceBriefTemplate } from "@shared/ads-manager";

export type PerformanceBriefKpiKey = "spend" | "metaPurchases" | "costPerPurchase" | "purchaseToClickRate" | "leads" | "costPerLead" | "leadRate";

export type PerformanceBriefCopy = {
  resultNoun: string;
  resultSingular: string;
  costPerResultLabel: string;
  resultsPerDayLabel: string;
  rateLabel: string;
  resultsCardLabel: string;
};

export type PerformanceBriefTemplateConfig = {
  label: string;
  sections: {
    accountOverview: boolean;
    metaReportedAov: boolean;
    reportBudgetCadence: boolean;
    budgetPacing: boolean;
    cacPacing: boolean;
    cplPacing: boolean;
    priorPeriodKeyChanges: boolean;
    spendDistribution: boolean;
    shopifyNewCustomerProductSales: boolean;
    metaEvents: boolean;
    budgetSettings: boolean;
  };
  kpiOrder: readonly PerformanceBriefKpiKey[];
  copy: PerformanceBriefCopy;
};

export const PERFORMANCE_BRIEF_TEMPLATE_CONFIG: Record<PerformanceBriefTemplate, PerformanceBriefTemplateConfig> = {
  ecommerce: {
    label: "Ecommerce",
    sections: {
      accountOverview: true,
      metaReportedAov: true,
      reportBudgetCadence: true,
      budgetPacing: true,
      cacPacing: true,
      cplPacing: false,
      priorPeriodKeyChanges: true,
      spendDistribution: true,
      shopifyNewCustomerProductSales: true,
      metaEvents: true,
      budgetSettings: true,
    },
    kpiOrder: ["spend", "metaPurchases", "costPerPurchase", "purchaseToClickRate"],
    copy: { resultNoun: "purchases", resultSingular: "purchase", costPerResultLabel: "Cost per purchase", resultsPerDayLabel: "Purchases per day", rateLabel: "Purchase-to-click rate", resultsCardLabel: "Meta purchases" },
  },
  lead_gen: {
    label: "Lead gen",
    sections: {
      accountOverview: true,
      metaReportedAov: false,
      reportBudgetCadence: true,
      budgetPacing: true,
      cacPacing: false,
      cplPacing: true,
      priorPeriodKeyChanges: true,
      spendDistribution: true,
      shopifyNewCustomerProductSales: false,
      metaEvents: false,
      budgetSettings: true,
    },
    kpiOrder: ["spend", "leads", "costPerLead", "leadRate"],
    copy: { resultNoun: "leads", resultSingular: "lead", costPerResultLabel: "Cost per lead", resultsPerDayLabel: "Leads per day", rateLabel: "Lead rate", resultsCardLabel: "Leads" },
  },
};

export function getPerformanceBriefTemplateConfig(value: unknown): PerformanceBriefTemplateConfig {
  return PERFORMANCE_BRIEF_TEMPLATE_CONFIG[resolvePerformanceBriefTemplate(value)];
}

export function isPerformanceBriefTargetApplicable(metric: string, template: PerformanceBriefTemplate): boolean {
  return template !== "lead_gen" || !["cost_per_purchase", "roas"].includes(metric);
}